import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { selectCurrentViewState } from '@common/store/navigation/selectors/viewstate.selectors';
import { getMediaTypeByURL } from '@common/utils/util';
import { DiscoverState, MediaType, SrcTypes, WireTypes } from '@discover/store/models/discover.models';
import { getIsNodeExpandable, getNodeChildren, getNodeLevel, nodeTransformer } from '@discover/utils/discover-search-result-utils';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { environment } from '@src/environments/environment';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { ResultGroup } from '@src/suite/common/data-models/search-results/result-group';
import { NodeType, TreeLikeFlatNode, TreeLikeNode } from '@src/suite/common/data-models/search-results/search-results';
import { APMediaEndpoint } from '@src/suite/common/enums/constants';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { selectGroupSortDefs } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectCurrentNodeUuid, selectNodeExpandState } from '@src/suite/common/store/list/list.selectors';
import { selectStartEndIndex } from '@src/suite/common/store/playbook/playbook.selector';

const selectDiscoverState = createFeatureSelector<DiscoverState>('discover');
export const selectWires = createSelector(selectDiscoverState, (state: DiscoverState) => {
    return state.wires;
});

export const selectIsLoading = createSelector(selectDiscoverState, (state: DiscoverState) => {
    return state.isLoading;
});
export const selectIsAttachToTopicListLoading = createSelector(selectDiscoverState, (state: DiscoverState) => state.isAttachToTopicListLoading);

export const selectGroupedSortedWires = createSelector(selectCurrentViewState, selectGroupSortDefs, selectWires, (currentViewState, groupSortDefs, wires) => {
    const groupBy = (currentViewState as StorytellingSingleViewStateRecord)?.PSTViewStateGrouping?.Value || PSTFieldNames.PSTWirePublished;
    const groupByOrder = (currentViewState as StorytellingSingleViewStateRecord)?.PSTViewStateGroupSortOrder || 'asc';
    const sortBy = (currentViewState as StorytellingSingleViewStateRecord)?.PSTViewStateSorting?.Value || PSTFieldNames.PSTWirePublished;

    const sortByOrder = (currentViewState as StorytellingSingleViewStateRecord)?.PSTViewStateSortOrder || 'asc';
    const wireGroup = new ResultGroup<StorytellingWireRecord>();
    const wireGroupingDef = groupSortDefs?.[groupBy];
    const wireSortingDef = groupSortDefs?.[sortBy];

    for (const wire of wires) {
        wireGroup.addToGroups(wireGroupingDef.valueConvertor(wire), wire);
    }
    wireGroup.sort(wireGroupingDef.groupKeyComparator(groupByOrder), wireSortingDef.recordComparator(sortByOrder));
    return wireGroup;
});

export const selectTreeDataSourceControlPair = createSelector(selectGroupedSortedWires, selectNodeExpandState, (wires, nodesExpandState) => {
    const treeControl: FlatTreeControl<TreeLikeFlatNode, string> = new FlatTreeControl<TreeLikeFlatNode, string>(getNodeLevel, getIsNodeExpandable, {
        trackBy: node => node.id,
    });
    const treeFlattener = new MatTreeFlattener<TreeLikeNode, TreeLikeFlatNode, string>(nodeTransformer, getNodeLevel, getIsNodeExpandable, getNodeChildren);
    // Populates our flattened data into the tree control
    const treeDataSource = new MatTreeFlatDataSource(treeControl, treeFlattener);
    const treeLikeData: TreeLikeNode[] = wires.keys.map(key => ({
        id: key.value,
        nodeType: NodeType.EXPANDER,
        uniqueId: key.value,
        label: key.display,
        children: wires.entries[key.value].map(entry => ({
            id: entry.Guid,
            nodeType: NodeType.RECORD,
            uniqueId: `${key.value}-${entry.Guid}`,
            children: [],
            object: entry,
        })),
    }));
    treeDataSource.data = treeLikeData;
    treeControl.expandAll();
    treeControl.dataNodes.forEach(node => {
        const state = nodesExpandState[node.id];
        if (state != null) {
            if (state === false) {
                treeControl.collapseDescendants(node);
            } else {
                treeControl.expandDescendants(node);
            }
        }
    });
    return { treeDataSource, treeControl };
});

export const selectTreeDataSource = createSelector(selectTreeDataSourceControlPair, pair => pair.treeDataSource);
export const selectTreeControl = createSelector(selectTreeDataSourceControlPair, pair => pair.treeControl);

export const selectCurrentCardItemHeights = createSelector(selectTreeControl, treeControl => {
    const heights = treeControl.dataNodes
        .map(item => {
            if (treeControl.expansionModel.isSelected(item.id)) {
                switch (item.level) {
                    case 0:
                        return 32;
                    default:
                        return 146;
                }
            }
            return item.level ? 0 : 32;
        })
        .filter(height => height > 0);
    return heights;
});

export const selectCurrentCondesedItemHeights = createSelector(selectTreeControl, treeControl => {
    const heights = treeControl.dataNodes
        .map(item => {
            if (treeControl.expansionModel.isSelected(item.id)) {
                switch (item.level) {
                    case 0:
                        return 32;
                    default:
                        return 60;
                }
            }
            return item.level ? 0 : 32;
        })
        .filter(height => height > 0);
    return heights;
});

export const selectSelectedWire = createSelector(selectTreeControl, selectCurrentNodeUuid, (treecontrol, uuid) => {
    return treecontrol.dataNodes.find(node => node.uniqueId === uuid)?.object as StorytellingWireRecord;
});

export const selectCurrentViewTopics = createSelector(selectDiscoverState, state => {
    return Array.from(state.topics?.values()) || [];
});

export const selectSelectedImages = createSelector(selectSelectedWire, wire => {
    const images: { url: string; mediaType: MediaType }[] = [];
    // This is a type guard to ensure we are dealing with a Discover Wire
    if (wire?.Type !== ObjectTypes._DISCOVER_WIRE) return images;

    let mediaType = wire?.Properties?.Type || wire?.Properties?.type;
    if (wire?.Properties?.MediaLinks) {
        if (!mediaType) {
            mediaType = getMediaTypeByURL(wire.Properties.MediaLinks);
        }
        if (mediaType) {
            images.push({ url: wire.Properties.MediaLinks, mediaType: mediaType });
        }
    } else if (wire?.WireType === WireTypes.ApWire && wire.Properties?.ThumbnailLink) {
        if (!mediaType) {
            mediaType = getMediaTypeByURL(wire.Properties.ThumbnailLink);
        }
        let srcType: SrcTypes = SrcTypes.ThumbnailLink;
        if (wire.Properties?.PreviewLink) {
            srcType = SrcTypes.PreviewLink;
        } else if (wire.Properties?.MainLink) {
            srcType = SrcTypes.MainLink;
        }
        if (mediaType) {
            images.push({ url: `${environment.discover_api_base_url}/${APMediaEndpoint}/${wire.Guid}/type/${srcType}`, mediaType: mediaType });
        }
    }
    return images;
});

export const selectImagesTotalCount = createSelector(selectSelectedImages, images => {
    return images?.length || 0;
});

export const selectTotalGroupedItemCount = createSelector(selectTreeControl, treeControl => {
    return treeControl.dataNodes.filter(node => node.level > 0)?.length || 0;
});

export const selectCurrentNodeIndex = createSelector(selectTreeControl, selectCurrentNodeUuid, (treeControl, currentNodeUuid) => {
    let idx = 0;
    treeControl.dataNodes.find(item => {
        if (item.level > 0) {
            idx++;
        }
        return item?.uniqueId === currentNodeUuid;
    });
    return idx;
});

export const selectTileViewGroups = createSelector(selectTreeDataSourceControlPair, wires => wires.treeDataSource.data);
export const selectTotalGroupCount = createSelector(selectTileViewGroups, wires => wires?.length ?? 0);

export const selectSavedViewStates = createSelector(selectDiscoverState, state => state.storedViewStates);
export const selectCurrentSelectedViewState = createSelector(selectDiscoverState, state => state.currentSelectedViewState);

export const selectDiscoverTileViewColumns = createSelector(selectTileViewGroups, selectStartEndIndex, (wires, columnRange) => {
    if (!wires) return [];
    return wires.slice(columnRange.startIndex, columnRange.endIndex);
});
